// Browser-only data adapter.  It deliberately mirrors the small response shape the
// UI used from the former network client, while keeping every record in localStorage.
import {
  mockCustomers, mockOrders, mockServices, mockStaff, mockPayments,
  mockPickups, mockDeliveries, mockBranches, mockDrivers, mockExpenses,
  mockAreas, mockNotifications, mockUsers,
} from './mockData';

const KEY = 'laundrymanagement-local-data-v1';
const clone = (value) => JSON.parse(JSON.stringify(value));
const seeds = {
  customers: mockCustomers, orders: mockOrders, services: mockServices, staff: mockStaff,
  payments: mockPayments, pickups: mockPickups, deliveries: mockDeliveries,
  branches: mockBranches, drivers: mockDrivers, expenses: mockExpenses,
  areas: mockAreas.map(name => ({ name })), notifications: mockNotifications, catalog: [],
};

const read = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY));
    if (stored && typeof stored === 'object') return { ...clone(seeds), ...stored };
  } catch (_) {}
  const initial = clone(seeds);
  localStorage.setItem(KEY, JSON.stringify(initial));
  return initial;
};
const write = (data) => localStorage.setItem(KEY, JSON.stringify(data));
const nextId = (items) => Math.max(0, ...items.map(x => Number(x.id) || 0)) + 1;
const resourceFor = (path) => path.split('?')[0].split('/').filter(Boolean)[0];
const idFor = (path) => decodeURIComponent(path.split('?')[0].split('/').filter(Boolean)[1] || '');
const findIndex = (items, id) => items.findIndex(x => String(x.id || x._id || x.key) === String(id));
const response = (data) => Promise.resolve({ data: clone(data) });

export const localApi = {
  get(path) {
    const data = read();
    if (path === '/branches/public') return response(data.branches.filter(b => b.status !== 'Inactive'));
    if (path.startsWith('/orders/public/')) {
      const id = decodeURIComponent(path.split('/').pop());
      return response(data.orders.find(o => String(o.id) === id || o.number === id) || null);
    }
    if (path.startsWith('/orders/purge-preview')) {
      const years = Number(new URLSearchParams(path.split('?')[1]).get('years')) || 2;
      const cutoffDate = new Date(Date.now() - years * 365.25 * 86400000).toISOString().slice(0, 10);
      const orders = data.orders.filter(o => String(o.createdAt || o.date || '') <= cutoffDate);
      return response({ years, cutoffDate, eligibleCount: orders.length, totalAmount: orders.reduce((n, o) => n + Number(o.totalAmount || 0), 0), orders });
    }
    if (path === '/reports/dashboard') return response(buildDashboard(data));
    if (path === '/reports/generate') return response({ data: data.orders });
    const resource = resourceFor(path);
    return response(data[resource] || []);
  },
  post(path, payload = {}) {
    const data = read();
    if (path === '/auth/login') {
      const user = mockUsers.find(u => u.email.toLowerCase() === String(payload.email).toLowerCase() && u.password === payload.password);
      if (!user) return Promise.reject(new Error('Login failed. Please verify credentials.'));
      return response({ user: (() => { const { password, ...safeUser } = user; return safeUser; })() });
    }
    if (path === '/auth/change-password') return response({ success: true });
    if (path === '/branches/restore-system-branches') {
      if (!data.branches.some(b => b.isSystemBranch)) data.branches.push(clone(mockBranches.find(b => b.isSystemBranch)));
      write(data); return response(data.branches);
    }
    if (path === '/orders/purge-old-invoices') {
      const years = Number(payload.years) || 2;
      const cutoff = new Date(Date.now() - years * 365.25 * 86400000).toISOString().slice(0, 10);
      const old = data.orders.filter(o => String(o.createdAt || o.date || '') <= cutoff);
      data.orders = data.orders.filter(o => !old.includes(o)); write(data); return response({ deletedCount: old.length });
    }
    if (path.includes('/settle')) {
      const customerId = path.split('/')[2];
      data.orders = data.orders.map(o => String(o.customerId) === String(customerId) ? { ...o, paymentStatus: 'Paid', amountPaid: o.totalAmount } : o);
      write(data); return response({ success: true });
    }
    const resource = resourceFor(path);
    if (!Array.isArray(data[resource])) return response({});
    const record = { ...payload, id: payload.id || nextId(data[resource]) };
    if (resource === 'orders' && !record.number) record.number = `ORD-${String(record.id).padStart(5, '0')}`;
    if (resource === 'areas') record.name = payload.name;
    data[resource].unshift(record); write(data); return response(record);
  },
  put(path, payload = {}) {
    const data = read();
    const resource = resourceFor(path); const id = idFor(path);
    if (path === '/orders/bulk/status') {
      data.orders = data.orders.map(o => payload.orderIds.map(String).includes(String(o.id)) ? { ...o, status: payload.status } : o); write(data); return response({ success: true });
    }
    if (path === '/orders/transfer') {
      data.orders = data.orders.map(o => payload.orderIds.map(String).includes(String(o.id)) ? { ...o, branchId: payload.targetBranchId } : o); write(data); return response({ message: 'Orders transferred' });
    }
    const items = data[resource] || []; const index = findIndex(items, id);
    if (index < 0) return response(payload);
    let record = { ...items[index], ...payload };
    if (path.endsWith('/lock')) record.isLocked = payload.isLocked;
    if (path.endsWith('/status')) record.status = payload.status;
    if (path.endsWith('/payment-status')) record = { ...record, paymentStatus: payload.paymentStatus, amountPaid: payload.amountPaid };
    if (path.endsWith('/read')) record.read = true;
    if (path.endsWith('/assign')) record.assignedStaff = payload.assignedStaff;
    items[index] = record; data[resource] = items; write(data); return response(record);
  },
  delete(path) {
    const data = read(); const resource = resourceFor(path); const id = idFor(path);
    if (Array.isArray(data[resource])) data[resource] = data[resource].filter(x => String(x.id || x._id || x.key || x.name) !== String(id));
    write(data); return response({ success: true });
  },
};

const buildDashboard = (data) => {
  const totalRevenue = data.orders.reduce((n, o) => n + Number(o.totalAmount || o.amount || 0), 0);
  const delivered = data.orders.filter(o => o.status === 'Delivered').length;
  return { summary: { totalRevenue, totalOrders: data.orders.length, completedOrders: delivered, pendingOrders: data.orders.length - delivered, activeCustomers: data.customers.length, averageOrderValue: data.orders.length ? totalRevenue / data.orders.length : 0 }, daily: {}, monthly: {}, orders: {}, payments: {}, logistics: {}, topCustomers: [], staffPerformance: [], paymentMethodBreakdown: [], orderStatusBreakdown: [] };
};

export const getLocalReceipt = (id) => {
  const data = read();
  return clone(data.orders.find(o => String(o.id) === String(id) || o.number === id) || null);
};
